import { ThemeProvider as MuiThemeProvider } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import React, { useMemo, useState } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";
import { Navbar } from "./components/persistent/Navbar";
import {
  ProtectedRoute,
  PublicOnlyRoute,
} from "./components/persistent/ProtectedRoute";
import { AuthProvider } from "./contexts/AuthContext";
import { createAppTheme } from "./lib/theme/theme";
import ConfirmAccountPage from "./pages/ConfirmAccountPage";
import DecisionNode from "./pages/DecisionNode";
import DecisionTreeList from "./pages/DecisionTreeList";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import SettingsPage from "./pages/SettingsPage";

// Component to handle legacy routes outside theme provider
const LegacyRouteChecker: React.FC = () => {
  const location = useLocation();
  let id = location.state?.treeId ?? "";

  // Check for query string
  if (!id) {
    const params = new URLSearchParams(location.search);
    id = params.get("treeId") ?? "";
  }

  // Check for path param
  if (!id && location.pathname.startsWith("/editor/")) {
    id = location.pathname.split("/editor/")[1] ?? "";
  }

  if (!id && location.pathname === "/editor") {
    id = "new"; // Default to new tree if no ID provided
  }

  if (
    location.pathname === "/editor" ||
    location.pathname.startsWith("/legacy") ||
    location.pathname.startsWith("/editor/")
  ) {
    return (
      <ProtectedRoute>
        <DecisionNode selectedId={id} />
      </ProtectedRoute>
    );
  }
  return null;
};

// Main app content with theme provider
const ThemedAppContent: React.FC<{
  mode: "light" | "dark";
  toggleTheme: () => void;
}> = ({ mode, toggleTheme }) => {
  const location = useLocation();

  // Skip theme provider for legacy routes
  if (
    location.pathname === "/editor" ||
    location.pathname.startsWith("/legacy")
  ) {
    return null;
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
      }}
    >
      <main
        style={{
          flex: 1,
          overflow: "auto",
          background: `
             linear-gradient(45deg, rgba(190, 212, 254, 1) 0%, rgba(231, 239, 254, 1) 20%, rgba(231, 239, 254, 1) 80%, rgba(190, 212, 254, 1) 100%)
          `,
          color: "text.primary",
          minHeight: "100vh",
        }}
      >
        {/* use theme.mixins.toolbar so spacer height exactly matches AppBar */}
        <Navbar mode={mode} toggleTheme={toggleTheme} />
        <Routes>
          <Route path="/" element={<Navigate to="/trees" replace />} />
          <Route
            path="/login"
            element={
              <PublicOnlyRoute>
                <LoginPage />
              </PublicOnlyRoute>
            }
          />
          <Route
            path="/signup"
            element={
              <PublicOnlyRoute>
                <SignupPage />
              </PublicOnlyRoute>
            }
          />
          <Route
            path="/confirm"
            element={
              <PublicOnlyRoute>
                <ConfirmAccountPage />
              </PublicOnlyRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trees"
            element={
              <ProtectedRoute>
                <DecisionTreeList />
              </ProtectedRoute>
            }
          />
        </Routes>
      </main>
    </div>
  );
};

export const App: React.FC = () => {
  const [mode, setMode] = useState<"light" | "dark">("light");
  const theme = useMemo(() => createAppTheme(mode), [mode]);

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
  };

  return (
    <AuthProvider>
      <Router>
        {/* Legacy routes rendered outside theme provider */}
        <LegacyRouteChecker />

        {/* Regular routes with theme provider */}
        <MuiThemeProvider theme={theme}>
          <CssBaseline />
          <ThemedAppContent mode={mode} toggleTheme={toggleTheme} />
        </MuiThemeProvider>
      </Router>
    </AuthProvider>
  );
};

export default App;
